import { ArrowRightIcon, CheckIcon, CubeTransparentIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { ProductVideo } from '../components/marketing/ProductVideo';
import { Cluster, Grid, Inline, Split, Stack } from '../components/ui';
import { featurePillars, marketingMedia } from '../lib/marketing';

const mediaById = Object.fromEntries(marketingMedia.map((item) => [item.id, item]));

function media(id: string) {
  const item = mediaById[id];
  if (!item) throw new Error(`Missing marketing media: ${id}`);
  return item;
}

const nextLayers = [
  {
    title: 'Build your own team workflows',
    description: 'Choose ready-made steps, forms, approvals, and actions in a guided visual builder.',
  },
  {
    title: 'Ask AI to create the first draft',
    description: 'Describe a feature request and receive a working draft your team can try, improve, and approve.',
  },
  {
    title: 'Add approved tools to your team menu',
    description: 'Publish an organization-specific workflow so it appears naturally inside the product your team uses.',
  },
  {
    title: 'Work anywhere, even offline',
    description: 'Capture field work without a connection and safely sync it when the device comes back online.',
  },
  {
    title: 'More industry-ready starting points',
    description: 'Switch language, records, forms, and starter workflows for new industries without starting over.',
  },
];

export function FeaturesPage(): JSX.Element {
  return (
    <div className="space-y-28 sm:space-y-36">
      <header className="glass-card relative overflow-hidden px-6 py-12 sm:px-10 sm:py-16 lg:px-14">
        <div
          className="absolute -right-28 -top-36 h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle_at_center,hsl(278_94%_72%/.34),transparent_68%)] blur-2xl"
          aria-hidden
        />
        <Split
          ratio="0.88:1.12"
          gap={10}
          align="center"
          className="relative"
          primary={
            <div className="space-y-6">
              <span className="badge-soft">
                <CubeTransparentIcon className="h-4 w-4" /> One place to build and launch
              </span>
              <div className="space-y-4">
                <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-[3.35rem] lg:leading-[1.04]">
                  Turn a feature idea into a workflow your team can actually use.
                </h1>
                <p className="text-lg text-muted-foreground">
                  Create the form, decide who can use it, test the experience, and launch a safe new version—all in
                  one place. AI helps with the heavy lifting while your team stays in control.
                </p>
              </div>
              <Cluster gap={3}>
                <Link to="/signup" className="btn-glass btn-primary focus-ring text-sm">
                  Start building <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link to="/security" className="btn-glass btn-secondary focus-ring text-sm">
                  See the control model
                </Link>
              </Cluster>
            </div>
          }
          secondary={<ProductVideo media={media('template-creation')} eager />}
        />
      </header>

      <section className="space-y-10">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <span className="badge-soft">Available today</span>
          <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
            Everything your team needs to build, test, and launch.
          </h2>
          <p className="text-muted-foreground">
            Turn a request into a real working experience without waiting for a long development backlog. Start small,
            review the result, and release it when your team is ready.
          </p>
        </div>

        <Grid columns={{ base: 1, md: 2, xl: 3 }} gap={5}>
          {featurePillars.map((pillar) => (
            <Stack as="article" gap={5} key={pillar.title} className="glass-card h-full p-6">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{pillar.eyebrow}</p>
                <h3 className="text-xl font-semibold text-foreground">{pillar.title}</h3>
                <p className="text-sm text-muted-foreground">{pillar.description}</p>
              </div>
              <ul className="mt-auto space-y-3 text-sm text-muted-foreground">
                {pillar.bullets.map((bullet) => (
                  <Inline as="li" gap={3} align="stretch" key={bullet}>
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <CheckIcon className="h-3.5 w-3.5" />
                    </span>
                    <span>{bullet}</span>
                  </Inline>
                ))}
              </ul>
            </Stack>
          ))}
        </Grid>
      </section>

      <Grid as="section" columns={{ base: 1, lg: 2 }} gap={10}>
        {[media('access-and-roles'), media('governed-agent-plan')].map((item) => (
          <article key={item.id} className="space-y-5">
            <ProductVideo media={item} />
            <div className="px-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{item.eyebrow}</p>
              <h2 className="mt-2 text-2xl font-semibold text-foreground">{item.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
            </div>
          </article>
        ))}
      </Grid>

      <section className="glass-card px-6 py-10 sm:px-10 lg:px-12">
        <Split
          ratio="0.72:1.28"
          gap={10}
          align="start"
          primary={
            <div className="space-y-5">
              <span className="badge-soft">
                <SparklesIcon className="h-4 w-4" /> Coming next
              </span>
              <h2 className="text-3xl font-semibold text-foreground">From smart forms to customer-built tools.</h2>
              <p className="text-sm text-muted-foreground">
                DefectSnap is being built so customers can create more than forms. The next releases bring guided
                workflow creation, AI-assisted feature drafts, team-specific tools, and reliable offline field work.
              </p>
            </div>
          }
          secondary={
            <Stack gap={4}>
              {nextLayers.map((layer, index) => (
                <Inline as="article" gap={4} align="stretch" key={layer.title} className="surface-panel p-5">
                  <span className="text-xs font-semibold tracking-[0.2em] text-primary">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground">{layer.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{layer.description}</p>
                  </div>
                </Inline>
              ))}
            </Stack>
          }
        />
      </section>
    </div>
  );
}
